import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GaugeChart from "@/components/growth/GaugeChart";
import { Trophy, TrendingUp, GitCompare, Lightbulb, ArrowUpDown, RefreshCw } from "lucide-react";
import { usePartnerMetrics } from "@/hooks/useGrowthMarketing";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getEdgeFunctionErrorMessage } from "@/lib/validation";

type Tab = "leaderboard" | "detail" | "attribution" | "insights";

interface Partner {
  id: string;
  name: string;
  type: string;
  referrals: number;
  conversions: number;
  revenue: number;
  health: number;
}

export default function PartnerPerformance() {
  const [tab, setTab] = useState<Tab>("leaderboard");
  const [sortField, setSortField] = useState<keyof Partner>("revenue");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [refreshing, setRefreshing] = useState(false);
  const { items: dbMetrics, loading, refresh } = usePartnerMetrics();

  const PARTNERS: Partner[] = useMemo(() => {
    return dbMetrics.map((m) => ({
      id: m.id,
      name: m.partner_name,
      type: m.partner_type ?? "Other",
      referrals: m.referrals,
      conversions: m.conversions,
      revenue: Number(m.revenue),
      health: m.health_score,
    }));
  }, [dbMetrics]);

  const [selected, setSelected] = useState<Partner | null>(null);
  const activeSelected = selected ?? PARTNERS[0];

  const triggerSnapshot = async () => {
    setRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke("snapshot-partner-metrics");
      if (error) throw error;
      await refresh();
      toast.success("Snapshot refreshed");
    } catch (e: any) {
      toast.error(await getEdgeFunctionErrorMessage(e, "Snapshot failed"));
    } finally {
      setRefreshing(false);
    }
  };

  const sorted = useMemo(() => {
    return [...PARTNERS].sort((a, b) => {
      const av = a[sortField] as any;
      const bv = b[sortField] as any;
      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [PARTNERS, sortField, sortDir]);

  const toggleSort = (f: keyof Partner) => {
    if (sortField === f) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(f); setSortDir("desc"); }
  };

  const totalRevenue = useMemo(() => PARTNERS.reduce((s, p) => s + p.revenue, 0), [PARTNERS]);

  const insights = useMemo(() => {
    const result: { title: string; body: string }[] = [];

    if (PARTNERS.length > 0 && totalRevenue > 0) {
      const topPerformer = PARTNERS.reduce((best, p) => (p.revenue > best.revenue ? p : best));
      const pct = ((topPerformer.revenue / totalRevenue) * 100).toFixed(0);
      result.push({ title: "Top performer", body: `${topPerformer.name} drives ${pct}% of revenue. Consider a quarterly thank-you visit.` });
    }

    const atRisk = PARTNERS.filter(p => p.health < 50).sort((a, b) => a.health - b.health)[0];
    if (atRisk) {
      result.push({ title: "At-risk partner", body: `${atRisk.name} health dropped to ${atRisk.health}. Schedule a re-engagement call.` });
    }

    const withReferrals = PARTNERS.filter(p => p.referrals > 0);
    if (withReferrals.length > 1) {
      const hiddenGem = withReferrals.reduce((best, p) =>
        (p.conversions / p.referrals) > (best.conversions / best.referrals) ? p : best);
      const rate = ((hiddenGem.conversions / hiddenGem.referrals) * 100).toFixed(0);
      result.push({ title: "Hidden gem", body: `${hiddenGem.name} has a ${rate}% conversion rate — highest in the network. Invest more here.` });
    }

    return result;
  }, [PARTNERS]);

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "leaderboard", label: "Leaderboard", icon: Trophy },
    { key: "detail", label: "Detail", icon: TrendingUp },
    { key: "attribution", label: "Attribution", icon: GitCompare },
    { key: "insights", label: "Insights", icon: Lightbulb },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "leaderboard" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Referral partners</CardTitle>
              <Button variant="outline" size="sm" onClick={triggerSnapshot} disabled={refreshing}>
                <RefreshCw className={`h-3.5 w-3.5 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-8">Loading partner data…</p>
            ) : PARTNERS.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No referral partner activity yet. Save some referrals from Find Referrals, then hit Refresh to generate a snapshot.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2"><button onClick={() => toggleSort("name")} className="flex items-center gap-1">Partner <ArrowUpDown className="h-3 w-3" /></button></th>
                      <th className="py-2">Type</th>
                      <th className="py-2"><button onClick={() => toggleSort("referrals")} className="flex items-center gap-1">Referrals <ArrowUpDown className="h-3 w-3" /></button></th>
                      <th className="py-2"><button onClick={() => toggleSort("conversions")} className="flex items-center gap-1">Conv. <ArrowUpDown className="h-3 w-3" /></button></th>
                      <th className="py-2"><button onClick={() => toggleSort("revenue")} className="flex items-center gap-1">Revenue <ArrowUpDown className="h-3 w-3" /></button></th>
                      <th className="py-2">Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((p) => (
                      <tr key={p.id} onClick={() => { setSelected(p); setTab("detail"); }} className="border-b last:border-0 cursor-pointer hover:bg-muted/30">
                        <td className="py-2.5 font-medium">{p.name}</td>
                        <td className="py-2.5"><Badge variant="outline" className="text-xs">{p.type}</Badge></td>
                        <td className="py-2.5">{p.referrals}</td>
                        <td className="py-2.5">{p.conversions}</td>
                        <td className="py-2.5">${(p.revenue / 1000).toFixed(0)}k</td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className={`h-full ${p.health >= 80 ? "bg-success" : p.health >= 50 ? "bg-warning" : "bg-destructive"}`} style={{ width: `${p.health}%` }} />
                            </div>
                            <span className="text-xs">{p.health}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "detail" && (!activeSelected ? (
        <Card><CardContent className="pt-6 text-sm text-muted-foreground text-center py-8">No partner selected. Pick one from the Leaderboard tab.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-1">
            <CardHeader><CardTitle className="text-base">{activeSelected.name}</CardTitle></CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              <GaugeChart value={activeSelected.health} label="Relationship Health" />
              <Badge variant="outline">{activeSelected.type}</Badge>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader><CardTitle className="text-base">Performance</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div><div className="text-xs text-muted-foreground">Referrals</div><div className="text-2xl font-bold text-navy">{activeSelected.referrals}</div></div>
                <div><div className="text-xs text-muted-foreground">Conversions</div><div className="text-2xl font-bold text-navy">{activeSelected.conversions}</div></div>
                <div><div className="text-xs text-muted-foreground">Revenue</div><div className="text-2xl font-bold text-navy">${(activeSelected.revenue / 1000).toFixed(0)}k</div></div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gold inline-block" /> Referrals</span>
                    <span>{activeSelected.referrals}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${Math.min(100, (activeSelected.referrals / Math.max(1, Math.max(...PARTNERS.map(p => p.referrals)))) * 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-navy inline-block" /> Conversions</span>
                    <span>{activeSelected.conversions}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-navy" style={{ width: `${Math.min(100, (activeSelected.conversions / Math.max(1, Math.max(...PARTNERS.map(p => p.conversions)))) * 100)}%` }} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  {activeSelected.referrals > 0
                    ? `${((activeSelected.conversions / activeSelected.referrals) * 100).toFixed(0)}% conversion rate over the trailing 30 days.`
                    : "No referrals recorded for this partner in the trailing 30 days."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      ))}

      {tab === "attribution" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Revenue attribution</CardTitle></CardHeader>
          <CardContent>
            {totalRevenue === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No revenue recorded yet for these partners.</p>
            ) : (
              <>
                <svg viewBox="0 0 200 200" className="w-48 h-48 mx-auto">
                  {/* Simple donut */}
                  {(() => {
                    let offset = 0;
                    const colors = ["hsl(var(--gold))", "hsl(var(--navy))", "hsl(38 92% 50%)", "hsl(199 89% 48%)", "hsl(142 71% 45%)"];
                    return PARTNERS.map((p, i) => {
                      const pct = (p.revenue / totalRevenue) * 100;
                      const circ = 2 * Math.PI * 80;
                      const dash = (pct / 100) * circ;
                      const el = <circle key={p.id} r="80" cx="100" cy="100" fill="transparent" stroke={colors[i % colors.length]} strokeWidth="30" strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset} transform="rotate(-90 100 100)" />;
                      offset += dash;
                      return el;
                    });
                  })()}
                </svg>
                <div className="space-y-1 mt-4">
                  {PARTNERS.map((p) => {
                    const pct = ((p.revenue / totalRevenue) * 100).toFixed(0);
                    return (
                      <div key={p.id} className="flex items-center justify-between py-1 text-sm">
                        <span>{p.name}</span><span className="text-muted-foreground">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "insights" && (
        <div className="space-y-3">
          {insights.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                Not enough partner activity yet to generate insights.
              </CardContent>
            </Card>
          ) : (
            insights.map((i) => (
              <Card key={i.title}>
                <CardContent className="pt-6 flex gap-3">
                  <Lightbulb className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{i.title}</div>
                    <div className="text-sm text-muted-foreground mt-1">{i.body}</div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
